/**
 * Add-On Selector Component
 *
 * Allows users to select optional add-ons
 */

import { motion } from 'framer-motion';
import type { AddOn } from './types';
import { StockIndicator } from './StockIndicator';

interface AddOnSelectorProps {
  addons: AddOn[];
  selectedAddOnIds: string[];
  onToggle: (addon: AddOn) => void;
  category: string;
  title: string;
  description?: string;
}

export const AddOnSelector = ({
  addons,
  selectedAddOnIds,
  onToggle,
  category,
  title,
  description,
}: AddOnSelectorProps) => {
  const categoryAddons = addons.filter((addon) => addon.category === category);

  if (categoryAddons.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
      </div>

      <div className="space-y-3">
        {categoryAddons.map((addon) => {
          const isSelected = selectedAddOnIds.includes(addon.id);
          const isOutOfStock = addon.stock === 0;

          return (
            <motion.button
              key={addon.id}
              onClick={() => !isOutOfStock && onToggle(addon)}
              disabled={isOutOfStock}
              whileHover={!isOutOfStock ? { scale: 1.01 } : {}}
              whileTap={!isOutOfStock ? { scale: 0.99 } : {}}
              className={`
                w-full p-4 border-2 rounded-lg transition-all text-left
                ${isSelected
                  ? 'border-brand-primary bg-brand-primary/5'
                  : 'border-gray-200 hover:border-brand-primary/30'
                }
                ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <div className="flex items-start justify-between">
                {/* Left Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {/* Checkbox */}
                    <div
                      className={`
                        w-5 h-5 border-2 rounded flex items-center justify-center
                        ${isSelected
                          ? 'bg-brand-primary border-brand-primary'
                          : 'border-gray-300'
                        }
                      `}
                    >
                      {isSelected && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Name */}
                    <span className="font-medium text-gray-900">{addon.name}</span>
                  </div>

                  {/* Description */}
                  {addon.description && (
                    <p className="text-sm text-gray-600 ml-8">{addon.description}</p>
                  )}

                  {/* Stock */}
                  <div className="ml-8 mt-2">
                    <StockIndicator stock={addon.stock} size="sm" />
                  </div>
                </div>

                {/* Price */}
                <div className="ml-4 flex flex-col items-end">
                  {addon.price === 0 ? (
                    <span className="text-sm font-medium text-green-600">Included</span>
                  ) : (
                    <span className="text-lg font-bold text-gray-900">
                      +${addon.price.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>

              {/* Out of Stock Overlay */}
              {isOutOfStock && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                  <span className="font-semibold text-gray-500">Out of Stock</span>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};
